import React, { useState } from "react";

const getMapName = (map) => (typeof map === "string" ? map : map.name);
const getMapId = (map) => {
  if (typeof map === "string") {
    return map.toLowerCase().replace(/\s+/g, "_");
  }

  return map.id || getMapName(map).toLowerCase().replace(/\s+/g, "_");
};
const normalizeMapName = (name) => String(name || "").replace(/[^a-z0-9]/gi, "").toLowerCase();
const getMapDataFileName = (mapName) => mapName
  .split(/[_\s-]+/)
  .filter(Boolean)
  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
  .join("");

const MapCard = ({ map, selected, disabled, onToggle }) => (
  <button
    className={`map-card ${selected ? "selected" : ""}`}
    onClick={() => onToggle(map.id)}
    aria-pressed={selected}
    disabled={disabled}
  >
    {map.name}
  </button>
);

const ModeCard = ({ mode, selected, onToggle }) => (
  <button
    className={`map-card ${selected ? "selected" : ""}`}
    onClick={() => onToggle(mode.id)}
    aria-pressed={selected}
  >
    {mode.name}
  </button>
);

const GameHome = ({ onPlay, onBackToGameSelect, game }) => {
  const [modeList, setModeList] = useState([]);
  const [mapList, setMapList] = useState([]);
  const [selectedMode, setSelectedMode] = useState([]);
  const [selectedMap, setSelectedMap] = useState([]);

  // Keep the raw JSON structures in refs for lookups without triggering re-renders
  const modesDataRef = React.useRef({});
  const allMapsRef = React.useRef([]);


  React.useEffect(() => {
    let mounted = true;
    const loadModesAndMaps = async () => {
      if (!game) {
        setModeList([]);
        setMapList([]);
        setSelectedMode([]);
        setSelectedMap([]);
        modesDataRef.current = {};
        allMapsRef.current = [];
        return;
      }

      try {
        // Load JSON via dynamic import so it is bundled with the app instead of fetching a public path
        let data;
        try {
          data = (await import(`../../data/maps/${game}/modes-and-maps.json`)).default;
        } catch (err) {
          throw new Error(`Failed to load /src/data/maps/${game}/modes-and-maps.json: ${err.message}`);
        }

        if (!mounted) return;

        // `data.modes` is an object keyed by mode (e.g. "hardpoint", "search_and_destroy", "overload")
        const modesObj = data.modes || {};
        const MODE_DISPLAY_NAMES = {
          hardpoint: "Hardpoint",
          search_and_destroy: "Search & Destroy",
          overload: "Overload",
        };
        const modes = Object.keys(modesObj).map((k) => ({
          id: k,
          name: MODE_DISPLAY_NAMES[k] || k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
        }));

        modesDataRef.current = modesObj;

        const allMaps = Array.isArray(data.all_maps) ? data.all_maps : [];
        const mapsArr = game === "mw4"
          ? await Promise.all(allMaps.map(async (item) => {
              const mapData = (await import(`../../data/maps/${game}/${getMapDataFileName(getMapName(item))}.json`)).default;
              return {
                id: mapData.id,
                name: mapData.label,
                disabled: false,
              };
            }))
          : allMaps.map((item) => ({
              id: getMapId(item),
              name: getMapName(item),
              disabled: false,
            }));
        allMapsRef.current = mapsArr;

        setModeList(modes);
        setMapList(mapsArr);
        // reset selections when game changes / data loads
        setSelectedMode([]);
        setSelectedMap([]);
      } catch (err) {
        console.error(err);
        if (!mounted) return;
        setModeList([]);
        setMapList([]);
      }
    };

    loadModesAndMaps();
    return () => {
      mounted = false;
    };
  }, [game]);

  const toggleMode = (id) => {
      setSelectedMode((prev) => (prev.includes(id) ? [] : [id]));
  }

  const toggleMap = (id) => {
      // prevent toggling disabled maps (button will also be disabled, this is a safety guard)
      const map = mapList.find((m) => m.id === id);
      if (map?.disabled) return;
      setSelectedMap((prev) => (prev.includes(id) ? [] : [id]));
  };

  // When the selected mode changes, update mapList to reflect allowed/disabled maps
  React.useEffect(() => {
    const allItems = allMapsRef.current || [];
    if (!allItems.length) return;

    if (selectedMode.length !== 1) {
      setMapList(allItems.map((item) => ({
        id: getMapId(item),
        name: getMapName(item),
        disabled: false,
      })));
      return;
    }

    const modeKey = selectedMode[0];
    const allowedNames = modesDataRef.current[modeKey] || [];
    const allowed = allowedNames.map((name) => {
      const matchingMap = allItems.find(
        (item) => normalizeMapName(getMapName(item)) === normalizeMapName(getMapName(name))
      );
      return matchingMap ? getMapId(matchingMap) : getMapId(name);
    });
    setMapList(allItems.map((item) => ({
      id: getMapId(item),
      name: getMapName(item),
      disabled: !allowed.includes(getMapId(item)),
    })));
  }, [selectedMode]);

  // If the currently selected map isn't allowed anymore, clear it
  React.useEffect(() => {
    const allItems = allMapsRef.current || [];
    if (!allItems.length || selectedMode.length !== 1 || selectedMap.length !== 1) return;

    const modeKey = selectedMode[0];
    const allowedNames = modesDataRef.current[modeKey] || [];
    const allowed = allowedNames.map((name) => {
      const matchingMap = allItems.find(
        (item) => normalizeMapName(getMapName(item)) === normalizeMapName(getMapName(name))
      );
      return matchingMap ? getMapId(matchingMap) : getMapId(name);
    });
    const selectedItem = allItems.find((item) => getMapId(item) === selectedMap[0]);
    if (selectedItem && !allowed.includes(getMapId(selectedItem))) setSelectedMap([]);
  }, [selectedMode, selectedMap]);

  const play = () => {
    if (selectedMode.length === 1 && selectedMap.length === 1) {
      const selectedItem = allMapsRef.current.find((item) => getMapId(item) === selectedMap[0]);
      onPlay({
        mode: selectedMode[0],
        map: selectedMap[0],
        ...(game === "mw4" && selectedItem ? { mapLabel: getMapName(selectedItem) } : {}),
      });
    }
  };

  return (
    <div className="game-home">
      <div className="game-home-top">
        <button
          type="button"
          className="game-home-logo-button"
          aria-label="Return to game selection"
          onClick={onBackToGameSelect}
        >
          <img src="/games/TacMaps_Logo.png" alt="" width="100px" />
        </button>
        <div className="game-home-title">
          TacMaps
          </div>
        <div className="game-home-play">
          <button
            className="play-btn"
            aria-label="Play"
            disabled={selectedMode.length !== 1 || selectedMap.length !== 1}
            onClick={play}
          >
            <img src="/icons/Play.png" alt="" className="toolbar-icon" />
          </button>
        </div>
      </div>
      <div className="landing-bottom">
        <div className="landing-left">
          <div className="map-grid">
            {modeList.map((mode) => (
              <ModeCard key={mode.id} mode={mode} selected={selectedMode.includes(mode.id)} onToggle={toggleMode} />
      ))}
          </div>
        </div>
        <div className="landing-right">
          <div className="map-options-scroll">
            <div className="map-grid">
              {mapList.map((m) => (
                <MapCard key={m.id} map={m} selected={selectedMap.includes(m.id)} disabled={m.disabled} onToggle={toggleMap} />
              ))} 
            </div>
          </div>
        </div>

      </div>
      
    </div>
  );
};

export default GameHome;
